import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Typography, Link, useTheme, Checkbox, useMediaQuery, Avatar } from '@mui/material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StyledMenu } from '@ui/components/common/Menu';
import useActivityFrameStyles from './useActivityFrameStyles';
import { useTranslation } from 'react-i18next';
import { TaskCompletionStatusEnum } from '@application/enums/task-completion-status-enum';
const ActivityFrame = ({ icon, text, personName, personId, subText, metaLeft, metaRight, subTextIcon, variant = 'light', type = 'create-task', assignedUser = 'Zoe', assignedUsers = [], onTaskCreate, onUserAssign, availableUsers = [], activityId = null, taskStatus }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState(assignedUsers || []);
    const is1280to1900 = useMediaQuery(theme.breakpoints.between(1280, 1900));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(700));
    const styles = useActivityFrameStyles(is1280to1900, isSmallScreen);
    // Sync selectedUsers with assignedUsers prop
    useEffect(() => {
        setSelectedUsers(assignedUsers || []);
    }, [assignedUsers]);
    const handleDropdownClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const handleUserSelect = (userId) => {
        const newSelectedUsers = selectedUsers.includes(userId)
            ? selectedUsers.filter((id) => id !== userId)
            : [...selectedUsers, userId];
        setSelectedUsers(newSelectedUsers);
        if (activityId == null) {
            return;
        }
        onUserAssign?.(activityId, newSelectedUsers);
        // Keep menu open for multiple selections (don't call handleMenuClose)
    };
    // Create menu actions for user selection with checkboxes
    const userMenuActions = availableUsers.map((user) => ({
        id: user.id,
        text: user.name,
        to: () => handleUserSelect(user.id),
        selected: selectedUsers.includes(user.id),
        group: true,
        keepOpen: true,
        isCheckbox: true,
        icon: (_jsx(Checkbox, { size: 'small', checked: selectedUsers.includes(user.id), onChange: () => handleUserSelect(user.id) }))
    }));
    const getUserInitials = (user) => {
        if (!user?.name)
            return '';
        const names = user.name.trim().split(' ');
        if (names.length === 1) {
            return names[0][0]?.toUpperCase() || '';
        }
        return `${names[0][0] || ''}${names[names.length - 1][0] || ''}`.toUpperCase();
    };
    // Get selected user objects from availableUsers
    const getSelectedUserObjects = () => {
        return availableUsers.filter((user) => selectedUsers.includes(user.id));
    };
    const getStatusInfo = () => {
        const defaultStatus = TaskCompletionStatusEnum.New;
        const currentStatus = taskStatus ?? defaultStatus;
        switch (currentStatus) {
            case TaskCompletionStatusEnum.New:
                return {
                    label: t('tasks.new', { defaultValue: 'New' }),
                    circleColor: theme.palette.text.primary,
                    textColor: theme.palette.text.primary
                };
            case TaskCompletionStatusEnum.InProgress:
                return {
                    label: t('tasks.inProgress', { defaultValue: 'In progress' }),
                    circleColor: theme.palette.info.main,
                    textColor: theme.palette.info.main
                };
            case TaskCompletionStatusEnum.Completed:
                return {
                    label: t('tasks.completed', { defaultValue: 'Completed' }),
                    circleColor: theme.palette.success.main,
                    textColor: theme.palette.success.main
                };
            default:
                return {
                    label: t('tasks.new', { defaultValue: 'New' }),
                    circleColor: theme.palette.text.primary,
                    textColor: theme.palette.text.primary
                };
        }
    };
    const selectedUserObjects = getSelectedUserObjects();
    const renderTextWithPersonLink = () => {
        const trimmedPersonName = personName?.trim();
        if (!trimmedPersonName || !personId)
            return text;
        // Match case-insensitively to be resilient to backend/client casing differences.
        const index = text.toLowerCase().indexOf(trimmedPersonName.toLowerCase());
        if (index === -1)
            return text;
        const before = text.slice(0, index);
        const linkedText = text.slice(index, index + trimmedPersonName.length);
        const after = text.slice(index + trimmedPersonName.length);
        return (_jsxs(_Fragment, { children: [before, _jsx(Link, { href: `/people/${personId}`, underline: 'hover', color: 'primary', sx: { cursor: 'pointer', fontWeight: 500 }, onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/people/${personId}`);
                    }, children: linkedText }), after] }));
    };
    return (_jsxs(Box, { className: 'activity_frame', sx: {
            ...styles.activityFrame
        }, children: [_jsxs(Box, { className: 'box_top', sx: { ...styles.boxTop }, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: (theme) => theme.spacing(1.5),
                            flex: 1,
                            minWidth: 0
                        }, children: [subTextIcon && subTextIcon, _jsxs(Box, { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, flex: 1, minWidth: 0, children: [type !== 'create-task' && taskStatus !== undefined && (_jsx(_Fragment, { children: (() => {
                                            const statusInfo = getStatusInfo();
                                            return (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.75, children: [_jsx(Box, { sx: {
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            backgroundColor: statusInfo.circleColor,
                                                            flexShrink: 0
                                                        } }), _jsx(Typography, { variant: 'body2', sx: {
                                                            color: statusInfo.textColor
                                                        }, children: statusInfo.label })] }));
                                        })() })), _jsx(Typography, { variant: 'body1', color: theme.palette.grey[700], children: renderTextWithPersonLink() }), _jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, sx: { minHeight: 20 }, children: [metaLeft && (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: metaLeft })), metaLeft && metaRight && (_jsx(Box, { sx: {
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: theme.palette.primary.main,
                                                    flexShrink: 0,
                                                    mt: '2px'
                                                } })), metaRight && (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: metaRight }))] })] })] }), type === 'create-task' && (_jsx(Box, { children: _jsx(Button, { variant: 'outlined', color: 'primary', size: 'small', onClick: onTaskCreate, sx: {
                                whiteSpace: 'nowrap'
                            }, children: t('actions.createTask') }) })), type !== 'create-task' && taskStatus !== undefined && selectedUserObjects.length > 0 && (_jsx(Box, { onClick: taskStatus === TaskCompletionStatusEnum.Completed ? undefined : handleDropdownClick, sx: {
                            cursor: taskStatus === TaskCompletionStatusEnum.Completed ? 'default' : 'pointer',
                            opacity: taskStatus === TaskCompletionStatusEnum.Completed ? 0.5 : 1
                        }, children: _jsxs(Box, { display: 'flex', sx: {
                                position: 'relative',
                                minWidth: selectedUserObjects.length > 3
                                    ? 72 + String(selectedUserObjects.length - 3).length * 10
                                    : undefined,
                                pr: selectedUserObjects.length > 3 ? 1 : 0,
                                overflow: 'visible'
                            }, children: [selectedUserObjects.slice(0, 3).map((user, idx) => (_jsx(Avatar, { variant: 'rounded', sx: {
                                        width: 32,
                                        height: 32,
                                        border: '2px solid #fff',
                                        fontSize: '14px',
                                        fontWeight: 400,
                                        ml: idx === 0 ? 0 : '-8px',
                                        zIndex: 3 - idx,
                                        backgroundColor: theme.palette.background.neutral,
                                        color: theme.palette.action.active,
                                        borderRadius: '12px'
                                    }, children: getUserInitials(user) }, user.id))), selectedUserObjects.length > 3 && (_jsx(Avatar, { variant: 'rounded', sx: {
                                        height: 32,
                                        border: '2px solid #fff',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        backgroundColor: theme.palette.primary.lighter,
                                        color: theme.palette.primary.dark,
                                        ml: '-8px',
                                        zIndex: 0,
                                        // px: 1.5,
                                        overflow: 'visible',
                                        minWidth: 32,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        // boxSizing: 'border-box',
                                        borderRadius: '12px'
                                    }, children: `+${selectedUserObjects.length - 3}` }))] }) }))] }), type === 'task-assigned' && (_jsx(StyledMenu, { anchorEl: anchorEl, open: Boolean(anchorEl), onClose: handleMenuClose, actions: userMenuActions }))] }));
};
export default ActivityFrame;
